import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, API, fmtTRY, useAuth } from "../lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Check, RefreshCw, Edit3, CheckCircle2, Circle, Radio, FolderInput, Ban } from "lucide-react";
import Menu from "./Menu";

const CATEGORIES = [
  { key: "menu", label: "Menü", icon: "🍽️" },
  { key: "genel", label: "Genel Liste" },
  { key: "yiyecek", label: "Yiyecek" },
  { key: "icecek", label: "İçecekler" },
  { key: "temizlik", label: "Temizlik" },
  { key: "diger", label: "Diğer" },
];
const UNITS = ["adet", "paket", "kg", "lt"];

const emptyDraft = { category: "yiyecek", subcategory: "", name: "", brand: "", quantity: "", unit: "adet", unit_cost_try: "", description: "" };

// Backend'deki _effective_qty() ile birebir uyumlu:
// - excluded_from_cost ise 0
// - adet > 0 ise adet
// - adet=0 ama not'ta sayı varsa → not'taki sayıyı kullan
// - not'ta "gr"/"gram" varsa ve item unit boş/"kg" ise n/1000 (750 gr → 0.75)
// - not'ta "ml" ve unit boş/"lt" ise n/1000
// - not'ta "kg"/"kilo" ve unit "gr"/"g" ise n*1000
export const effectiveQty = (item) => {
  if (!item) return 0;
  if (item.excluded_from_cost) return 0;
  const q = parseFloat(item.quantity) || 0;
  if (q > 0) return q;
  const note = String(item.description || "").trim();
  if (!note) return 0;
  const m = note.match(/^\s*([0-9]+(?:[.,][0-9]+)?)/);
  if (!m) return 0;
  const n = parseFloat(m[1].replace(",", ".")) || 0;
  if (n === 0) return 0;
  const noteLow = note.toLowerCase();
  const unit = String(item.unit || "").toLowerCase().trim();
  const hasGr = /\b(gr|gram)\b/.test(noteLow) || /\bg\b(?!\w)/.test(noteLow);
  const hasKg = /\b(kg|kilo)\b/.test(noteLow);
  const hasMl = /\bml\b/.test(noteLow);
  const hasLt = /\b(lt|litre|l)\b/.test(noteLow);
  if (hasGr && (unit === "" || unit === "kg" || unit === "kilo")) return n / 1000;
  if (hasKg && (unit === "gr" || unit === "gram" || unit === "g")) return n * 1000;
  if (hasMl && (unit === "" || unit === "lt" || unit === "litre" || unit === "l")) return n / 1000;
  if (hasLt && unit === "ml") return n * 1000;
  return n;
};

// Item'ın toplam maliyetini döndürür (turuncu Toplam ₺ değeri)
// "Maliyet hesabından çıkar" işaretli ürünler 0 döner — PDF Özet ile senkron.
export const itemTotal = (item) => {
  if (item?.excluded_from_cost) return 0;
  return effectiveQty(item) * (parseFloat(item?.unit_cost_try) || 0);
};

const LS_KEY = "shopping.ui.v1";
const _loadLS = () => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

export default function Shopping() {
  const { user } = useAuth();
  const _ls = _loadLS();
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState(_ls.tab || "genel");
  const [subTab, setSubTab] = useState(_ls.subTab || "__all__");  // Yiyecek/İçecekler alt-grup
  const [showOtherSubGroups, setShowOtherSubGroups] = useState(false);  // Mobil "Diğer Gruplar" toggle
  const [openGroup, setOpenGroup] = useState(null);  // Genel Liste'de mobilde tek grup açık (accordion)
  const [search, setSearch] = useState(_ls.search || "");
  const [categoryTree, setCategoryTree] = useState({ yiyecek: [], icecek: [], temizlik: [], diger: [] });
  const [shoppingNotes, setShoppingNotes] = useState("");
  const [shoppingNotesEdit, setShoppingNotesEdit] = useState(false);
  const [shoppingNotesDraft, setShoppingNotesDraft] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(emptyDraft);
  const [showNew, setShowNew] = useState(false);
  const [estimating, setEstimating] = useState(false);
  const [estMsg, setEstMsg] = useState("");
  const [bulkEdit, setBulkEdit] = useState(false);
  const [bulkDraft, setBulkDraft] = useState({});
  const [lastSync, setLastSync] = useState(null);
  const [liveError, setLiveError] = useState(false);
  // Right-click context menu for changing group / subcategory of an item.
  const [groupMenu, setGroupMenu] = useState(null); // { item, x, y }
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const toggleExpand = (id) => setExpandedIds((prev) => {
    const nx = new Set(prev);
    if (nx.has(id)) nx.delete(id); else nx.add(id);
    return nx;
  });
  const itemsRef = useRef([]);
  const isBusyRef = useRef(false);
  const firstLoadRef = useRef(true);

  // Persist tab / subTab / search to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify({ tab, subTab, search }));
    } catch { /* ignore quota errors */ }
  }, [tab, subTab, search]);

  // Load category tree once
  useEffect(() => {
    api.get("/erzak/categories").then(({ data }) => {
      setCategoryTree(data?.tree || {});
    }).catch(() => {});
    api.get("/shopping-notes").then(({ data }) => {
      setShoppingNotes(data?.notes || "");
    }).catch(() => {});
  }, []);

  const saveShoppingNotes = async () => {
    try {
      await api.put("/shopping-notes", { notes: shoppingNotesDraft });
      setShoppingNotes(shoppingNotesDraft);
      setShoppingNotesEdit(false);
      toast.success("Alışveriş notları kaydedildi");
    } catch (e) {
      toast.error("Kaydedilemedi");
    }
  };

  // Reset subTab when main tab CHANGES (but respect persisted subTab on first mount)
  const _skipFirstTabChangeRef = useRef(true);
  useEffect(() => {
    if (_skipFirstTabChangeRef.current) {
      _skipFirstTabChangeRef.current = false;
      return;
    }
    setSubTab("__all__");
  }, [tab]);

  // Keep latest snapshot in a ref so polling can diff without stale closures
  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  // Skip overwriting items while user is actively editing/adding
  useEffect(() => {
    isBusyRef.current = bulkEdit || !!editingId || showNew;
  }, [bulkEdit, editingId, showNew]);

  const load = async ({ silent = false } = {}) => {
    try {
      const { data: rawData } = await api.get("/shopping");
      const data = Array.isArray(rawData) ? rawData : [];
      setLiveError(false);
      setLastSync(new Date());

      // Detect items newly marked done by *other* users (only after first load)
      if (!firstLoadRef.current && silent) {
        const prevById = new Map(itemsRef.current.map((x) => [x.id, x]));
        const newlyDoneByOthers = {};
        for (const it of data) {
          const prev = prevById.get(it.id);
          if (
            it.done &&
            (!prev || !prev.done) &&
            it.done_by &&
            it.done_by !== user?.username
          ) {
            const who = it.done_by_display || it.done_by;
            newlyDoneByOthers[who] = (newlyDoneByOthers[who] || 0) + 1;
          }
        }
        for (const [who, count] of Object.entries(newlyDoneByOthers)) {
          toast.success(
            count === 1
              ? `${who} bir ürünü hazır işaretledi`
              : `${who} ${count} ürünü hazır işaretledi`,
            { duration: 2500 }
          );
        }
      }
      firstLoadRef.current = false;

      // While the user is actively editing, don't overwrite local state
      if (silent && isBusyRef.current) return;
      setItems(data);
    } catch (e) {
      if (!silent) throw e;
      setLiveError(true);
    }
  };
  useEffect(() => {
    load();
    const id = setInterval(() => {
      if (document.visibilityState === "visible") {
        load({ silent: true });
      }
    }, 3000);
    const onVis = () => {
      if (document.visibilityState === "visible") load({ silent: true });
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Turkish-insensitive normalizer for search (i/I/ı/İ same, diacritics stripped)
  const normSearch = (s) =>
    (s || "")
      .toLocaleLowerCase("tr-TR")
      .replace(/i̇/g, "i")
      .replace(/[iıİI]/g, "i")
      .replace(/[şs]/g, "s")
      .replace(/[çc]/g, "c")
      .replace(/[ğg]/g, "g")
      .replace(/[üu]/g, "u")
      .replace(/[öo]/g, "o")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

  const filtered = useMemo(() => {
    let arr;
    if (tab === "genel") {
      // Genel Liste: tüm her şey (DİĞER dahil — PDF Özet ile senkron)
      arr = items;
    } else {
      // Kategoriye göre filtrele
      arr = items.filter((i) => i.category === tab);
      // Alt-grup seçilmişse onunla da filtrele
      if (subTab && subTab !== "__all__") {
        arr = arr.filter((i) => (i.subcategory || "") === subTab);
      }
    }
    const q = normSearch(search).trim();
    if (q) {
      arr = arr.filter((i) => {
        const hay = normSearch(
          [i.name, i.brand, i.unit, i.description, i.subcategory].filter(Boolean).join(" ")
        );
        return hay.includes(q);
      });
    }
    return arr;
  }, [items, tab, subTab, search]);

  // Build a display list that inserts subcategory group headers when the
  // "Tümü" sub-tab is active OR the Genel Liste (mixed cats) is shown. This
  // lets users see at a glance which items belong to which group.
  const displayList = useMemo(() => {
    const showHeaders = (tab === "genel") || (tab !== "menu" && subTab === "__all__");
    if (!showHeaders) return filtered.map((i) => ({ type: "item", item: i }));
    // Group items by subcategory, keeping first-encountered order.
    const groups = new Map();
    for (const i of filtered) {
      const sub = (i.subcategory || "").trim() || "—";
      if (!groups.has(sub)) groups.set(sub, []);
      groups.get(sub).push(i);
    }
    const out = [];
    for (const [sub, its] of groups.entries()) {
      out.push({ type: "header", label: sub, count: its.length });
      for (const it of its) out.push({ type: "item", item: it, groupLabel: sub });
    }
    return out;
  }, [filtered, tab, subTab]);

  // Accordion: mobil için ilk grupları default açık tut ilk load'da,
  // kullanıcı bir gruba basınca sadece o kalır açık.
  useEffect(() => {
    if (openGroup === null && displayList.length > 0) {
      const first = displayList.find((e) => e.type === "header");
      if (first) setOpenGroup(first.label);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayList]);

  // Highlight helper: returns JSX with the app's orange (primary) bg + white text
  // wrapping every occurrence of `query` inside `text`. Turkish-insensitive.
  const highlight = (text) => {
    const t = String(text ?? "");
    const q = search.trim();
    if (!q) return t;
    const nT = normSearch(t);
    const nQ = normSearch(q);
    if (!nQ || !nT.includes(nQ)) return t;
    // Walk through the original text char-by-char using a parallel index into
    // normalized text so we find match ranges in ORIGINAL indices.
    const parts = [];
    let ni = 0, oi = 0;
    let cursor = 0;
    while (oi < t.length) {
      const orig = t.slice(oi, oi + 1);
      const normed = normSearch(orig);
      if (normed && ni + nQ.length <= nT.length && nT.slice(ni, ni + nQ.length) === nQ) {
        // Find how many original chars this matched consumes so far
        let consumedOrig = 0;
        let consumedNorm = 0;
        while (consumedNorm < nQ.length && oi + consumedOrig < t.length) {
          const ch = t.slice(oi + consumedOrig, oi + consumedOrig + 1);
          const chN = normSearch(ch);
          consumedNorm += chN.length;
          consumedOrig += 1;
        }
        if (cursor < oi) parts.push(t.slice(cursor, oi));
        parts.push({ hl: t.slice(oi, oi + consumedOrig) });
        oi += consumedOrig;
        ni += consumedNorm;
        cursor = oi;
      } else {
        oi += 1;
        ni += normed.length;
      }
    }
    if (cursor < t.length) parts.push(t.slice(cursor));
    return parts.map((p, i) =>
      typeof p === "string" ? p : (
        <span key={i} data-testid="search-hit" style={{ backgroundColor: "hsl(var(--primary))", color: "white", padding: "1px 3px", borderRadius: 3 }}>{p.hl}</span>
      ),
    );
  };
  const catTotal = useMemo(() => filtered.reduce((s, i) => s + itemTotal(i), 0), [filtered]);
  // Round UP to nearest 250 ₺ (used in category summary tiles + Özet/Tam PDF).
  const roundUpTo250 = (n) => Math.ceil((n || 0) / 250) * 250;
  const catTotalRounded = useMemo(() => roundUpTo250(catTotal), [catTotal]);
  const catDoneCount = useMemo(() => filtered.filter((i) => i.done).length, [filtered]);
  const grandTotal = useMemo(() => items.reduce((s, i) => s + itemTotal(i), 0), [items]);
  // Ürün sayısı — PDF Özet has_data ile senkron: itemTotal > 0 VE excluded_from_cost=false
  const filteredKalemCount = useMemo(
    () => filtered.filter((i) => !i.excluded_from_cost && itemTotal(i) > 0).length,
    [filtered]
  );
  const grandKalemCount = useMemo(
    () => items.filter((i) => !i.excluded_from_cost && itemTotal(i) > 0).length,
    [items]
  );

  const toggleDone = async (i) => {
    await api.put(`/shopping/${i.id}`, {
      category: i.category,
      subcategory: i.subcategory || "",
      brand: i.brand || "",
      name: i.name,
      quantity: i.quantity || 0,
      unit: i.unit || "adet",
      unit_cost_try: i.unit_cost_try || 0,
      description: i.description || "",
      done: !i.done,
    });
    setItems((arr) => arr.map((x) => (x.id === i.id ? { ...x, done: !i.done } : x)));
  };

  const startBulkEdit = () => {
    const draft = {};
    for (const i of filtered) {
      draft[i.id] = {
        name: i.name || "",
        quantity: i.quantity ?? 0,
        unit: i.unit || "adet",
        unit_cost_try: i.unit_cost_try ?? 0,
        description: i.description || "",
      };
    }
    setBulkDraft(draft);
    setBulkEdit(true);
    setShowNew(false);
    setEditingId(null);
  };

  const cancelBulkEdit = () => {
    setBulkEdit(false);
    setBulkDraft({});
  };

  const saveBulkEdit = async () => {
    const updates = [];
    for (const i of filtered) {
      const d = bulkDraft[i.id];
      if (!d) continue;
      const newQty = parseFloat(String(d.quantity).replace(",", ".")) || 0;
      const newCost = parseFloat(String(d.unit_cost_try).replace(",", ".")) || 0;
      const newUnit = UNITS.includes(d.unit) ? d.unit : "adet";
      const newName = (d.name || "").trim();
      if (
        newName !== i.name ||
        newQty !== (i.quantity || 0) ||
        newCost !== (i.unit_cost_try || 0) ||
        newUnit !== (i.unit || "adet") ||
        (d.description || "") !== (i.description || "")
      ) {
        updates.push(
          api.put(`/shopping/${i.id}`, {
            category: i.category,
            subcategory: i.subcategory || "",
            brand: i.brand || "",
            name: newName || i.name,
            quantity: newQty,
            unit: newUnit,
            unit_cost_try: newCost,
            description: d.description || "",
          })
        );
      }
    }
    if (updates.length) await Promise.all(updates);
    setBulkEdit(false);
    setBulkDraft({});
    await load();
  };

  const updBulk = (id, k, v) => setBulkDraft((d) => ({ ...d, [id]: { ...d[id], [k]: v } }));

  // ------- Group / subcategory quick move (right-click context menu) -------
  const openGroupMenu = (e, item) => {
    const menuW = 300;
    const menuH = 380;
    const x = Math.min(e.clientX, (window.innerWidth || 1200) - menuW - 8);
    const y = Math.min(e.clientY, (window.innerHeight || 800) - menuH - 8);
    setGroupMenu({ item, x, y });
  };

  const moveItemToGroup = async (targetCat, targetSub) => {
    if (!groupMenu?.item) return;
    const i = groupMenu.item;
    if (i.category === targetCat && (i.subcategory || "") === (targetSub || "")) {
      setGroupMenu(null);
      return;
    }
    await api.put(`/shopping/${i.id}`, {
      category: targetCat,
      subcategory: targetSub || "",
      brand: i.brand || "",
      name: i.name,
      quantity: i.quantity || 0,
      unit: i.unit || "adet",
      unit_cost_try: i.unit_cost_try || 0,
      description: i.description || "",
      done: !!i.done,
    });
    setItems((arr) => arr.map((x) => (x.id === i.id ? { ...x, category: targetCat, subcategory: targetSub || "" } : x)));
    setGroupMenu(null);
  };

  // Close on outside click / Escape
  useEffect(() => {
    if (!groupMenu) return;
    const close = () => setGroupMenu(null);
    const onKey = (e) => { if (e.key === "Escape") setGroupMenu(null); };
    // Delay a tick so the same event that opened it doesn't immediately close it
    const t = setTimeout(() => {
      document.addEventListener("click", close);
      document.addEventListener("keydown", onKey);
    }, 0);
    return () => {
      clearTimeout(t);
      document.removeEventListener("click", close);
      document.removeEventListener("keydown", onKey);
    };
  }, [groupMenu]);

  const toggleExclude = async (i) => {
    const newVal = !i.excluded_from_cost;
    await api.put(`/shopping/${i.id}`, {
      category: i.category,
      subcategory: i.subcategory || "",
      brand: i.brand || "",
      name: i.name,
      quantity: i.quantity || 0,
      unit: i.unit || "adet",
      unit_cost_try: i.unit_cost_try || 0,
      description: i.description || "",
      done: !!i.done,
      excluded_from_cost: newVal,
    });
    setItems((arr) => arr.map((x) => (x.id === i.id ? { ...x, excluded_from_cost: newVal } : x)));
  };

  const startEdit = (i) => {
    setEditingId(i.id);
    setDraft({
      category: i.category,
      subcategory: i.subcategory || "",
      brand: i.brand || "",
      name: i.name,
      quantity: i.quantity,
      unit: i.unit || "",
      unit_cost_try: i.unit_cost_try || 0,
      description: i.description || "",
    });
    setShowNew(false);
  };

  const cancelEdit = () => { setEditingId(null); setDraft(emptyDraft); };

  const save = async () => {
    const body = {
      category: draft.category || tab,
      subcategory: draft.subcategory || "",
      brand: draft.brand || "",
      name: draft.name?.trim() || "",
      quantity: parseFloat(String(draft.quantity).replace(",", ".")) || 0,
      unit: UNITS.includes(draft.unit) ? draft.unit : "adet",
      unit_cost_try: parseFloat(String(draft.unit_cost_try).replace(",", ".")) || 0,
      description: draft.description || "",
    };
    if (!body.name) return;
    if (editingId) await api.put(`/shopping/${editingId}`, body);
    else await api.post(`/shopping`, body);
    setEditingId(null); setShowNew(false); setDraft(emptyDraft);
    await load();
  };

  const remove = async (id) => {
    if (!window.confirm("Bu satır silinsin mi?")) return;
    await api.delete(`/shopping/${id}`);
    await load();
  };

  const [pricePopup, setPricePopup] = useState(null); // {message, updated}
  const pricePopupTimerRef = useRef(null);
  const pendingPricePopupRef = useRef(null);

  // Auto-dismiss popup after 3s
  useEffect(() => {
    if (!pricePopup) return;
    if (pricePopupTimerRef.current) clearTimeout(pricePopupTimerRef.current);
    pricePopupTimerRef.current = setTimeout(() => setPricePopup(null), 3000);
    return () => clearTimeout(pricePopupTimerRef.current);
  }, [pricePopup]);

  // If popup was queued while tab hidden, show it when tab becomes visible again
  useEffect(() => {
    const onVis = () => {
      if (!document.hidden && pendingPricePopupRef.current) {
        setPricePopup(pendingPricePopupRef.current);
        pendingPricePopupRef.current = null;
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  const showPricePopup = (payload) => {
    if (document.hidden) {
      pendingPricePopupRef.current = payload;
    } else {
      setPricePopup(payload);
    }
  };

  const refreshPrices = async () => {
    setEstimating(true);
    setEstMsg("Fiyat tahmini başlatılıyor…");
    try {
      const { data } = await api.post("/shopping/estimate-prices");
      const jobId = data?.job_id;
      if (!jobId) {
        setEstMsg(data?.message || "İş oluşturulamadı");
        return;
      }
      // Poll every 3s, up to ~5 min
      let lastPct = 0;
      const started = Date.now();
      while (Date.now() - started < 5 * 60 * 1000) {
        await new Promise((r) => setTimeout(r, 3000));
        try {
          const { data: st } = await api.get(`/shopping/estimate-prices/status/${jobId}`);
          if (st.progress_pct && st.progress_pct !== lastPct) {
            lastPct = st.progress_pct;
          }
          setEstMsg(`${st.message || "Çalışıyor…"} · %${st.progress_pct || 0}`);
          if (st.status === "done") {
            setEstMsg(`${st.updated} kalem güncellendi.`);
            await load();
            showPricePopup({
              title: "Fiyatlar güncellendi",
              message: `${st.updated} ürünün birim fiyatı yenilendi.`,
            });
            break;
          }
          if (st.status === "error") {
            setEstMsg(`Hata: ${st.error || "bilinmeyen"}`);
            showPricePopup({
              title: "Fiyat güncellemesi başarısız",
              message: st.error || "Bilinmeyen hata",
              isError: true,
            });
            break;
          }
        } catch (e) {
          // ignore transient errors, keep polling
        }
      }
    } catch (e) {
      setEstMsg(e.response?.data?.detail || "Fiyat tahmini başarısız");
    } finally {
      setEstimating(false);
      setTimeout(() => setEstMsg(""), 6000);
    }
  };

  const [exporting, setExporting] = useState(false);
  const exportPdf = async (mode = "full") => {
    try {
      setExporting(true);
      const token = localStorage.getItem("tk_token");
      const res = await fetch(`${API}/shopping/export.pdf?mode=${mode}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("PDF oluşturulamadı");
      const blob = await res.blob();
      const cd = res.headers.get("content-disposition") || "";
      let filename = "";
      const utf8m = cd.match(/filename\*=UTF-8''([^;]+)/i);
      const asciim = cd.match(/filename="([^"]+)"/i);
      if (utf8m) { try { filename = decodeURIComponent(utf8m[1]); } catch { filename = utf8m[1]; } }
      else if (asciim) { filename = asciim[1]; }
      if (!filename) {
        const label = mode === "summary" ? "Özet" : mode === "boat" ? "Tekne" : "Tam";
        const d = new Date();
        const dd = String(d.getDate()).padStart(2, "0");
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const yy = String(d.getFullYear()).slice(-2);
        filename = `Aria - 22 Ağustos Grubu - Alışveriş Listesi - ${label} (${dd}${mm}${yy}).pdf`;
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) {
      toast.error("PDF indirilemedi: " + e.message);
    } finally {
      setExporting(false);
    }
  };

  const exportXlsx = async () => {
    try {
      setExporting(true);
      const token = localStorage.getItem("tk_token");
      const res = await fetch(`${API}/shopping/export.xlsx`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("Excel oluşturulamadı");
      const blob = await res.blob();
      const cd = res.headers.get("content-disposition") || "";
      let filename = "";
      const utf8m = cd.match(/filename\*=UTF-8''([^;]+)/i);
      const asciim = cd.match(/filename="([^"]+)"/i);
      if (utf8m) { try { filename = decodeURIComponent(utf8m[1]); } catch { filename = utf8m[1]; } }
      else if (asciim) { filename = asciim[1]; }
      if (!filename) filename = "Aria - Kumanya.xlsx";
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) {
      toast.error("Excel indirilemedi: " + e.message);
    } finally {
      setExporting(false);
    }
  };

  const exportMenuPdf = async () => {
    try {
      setExporting(true);
      const token = localStorage.getItem("tk_token");
      const res = await fetch(`${API}/menu/export.pdf`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("PDF oluşturulamadı");
      const blob = await res.blob();
      const cd = res.headers.get("content-disposition") || "";
      let filename = "";
      const utf8m = cd.match(/filename\*=UTF-8''([^;]+)/i);
      const asciim = cd.match(/filename="([^"]+)"/i);
      if (utf8m) { try { filename = decodeURIComponent(utf8m[1]); } catch { filename = utf8m[1]; } }
      else if (asciim) { filename = asciim[1]; }
      if (!filename) {
        const d = new Date();
        const dd = String(d.getDate()).padStart(2, "0");
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const yy = String(d.getFullYear()).slice(-2);
        filename = `Aria - 22 Ağustos Grubu - Menü (${dd}${mm}${yy}).pdf`;
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) {
      toast.error("PDF indirilemedi: " + e.message);
    } finally {
      setExporting(false);
    }
  };

  const shareWhatsAppMenu = async () => {
    // Menu WA sharing (embedded when tab === "menu")
    try {
      const { data } = await api.get("/menu");
      const days = Array.isArray(data?.days) ? data.days : [];
      let text = "*Mavi Yolculuk 26 · Yemek Menüsü*\n";
      text += "_22 – 29 Ağustos 2026 · 12 kişi_\n\n";
      const totals = {};
      for (const d of days) {
        const dish = (d.main_dish || "").trim();
        text += `*Gün ${d.day}* · ${d.date_label}\n${dish || "-"}${d.per_person_g ? ` · ${d.per_person_g}g/kişi` : ""}${d.rounded_kg ? ` · ${d.rounded_kg} kg` : ""}\n`;
        if (d.notes) text += `_${d.notes}_\n`;
        text += "\n";
        if (dish) {
          const key = dish.toLowerCase();
          if (!totals[key]) totals[key] = { name: dish, kg: 0 };
          totals[key].kg += Number(d.rounded_kg || d.total_kg || 0);
        }
      }
      if (Object.keys(totals).length) {
        text += "*Toplam Alışveriş (Yemek Bazlı)*\n";
        const sorted = Object.values(totals).sort((a, b) => b.kg - a.kg);
        let grand = 0;
        for (const t of sorted) {
          text += `• ${t.name}: ${t.kg.toFixed(1)} kg\n`;
          grand += t.kg;
        }
        text += `\n*GENEL TOPLAM: ${grand.toFixed(1)} kg*`;
      }
      const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
      window.open(url, "_blank");
    } catch (e) {
      toast.error("Menü paylaşılamadı: " + e.message);
    }
  };

  const shareWhatsApp = () => {
    // If menu tab, share menu; else share shopping list
    if (tab === "menu") { shareWhatsAppMenu(); return; }
    // Kategori bazlı özet metin
    const catLabels = { genel: "Genel Liste", aksam: "Akşam Yemeği", icecek: "İçecekler", diger: "Diğer" };
    let text = "*Mavi Yolculuk 26 · Alışveriş Listesi*\n\n";
    let grand = 0;
    for (const cat of ["genel", "aksam", "icecek", "diger"]) {
      const catItems = items.filter((i) => i.category === cat);
      if (!catItems.length) continue;
      text += `_${catLabels[cat]}_\n`;
      let catTotal = 0;
      for (const i of catItems) {
        const qty = Number(i.quantity || 0);
        const cost = Number(i.unit_cost_try || 0);
        const total = qty * cost;
        catTotal += total;
        text += `• ${i.name}: ${qty} ${i.unit}${cost ? ` × ${cost.toLocaleString("tr-TR")}₺ = ${total.toLocaleString("tr-TR", { maximumFractionDigits: 0 })}₺` : ""}\n`;
      }
      text += `→ Ara Toplam: *${catTotal.toLocaleString("tr-TR", { maximumFractionDigits: 0 })}₺*\n\n`;
      grand += catTotal;
    }
    text += `*GENEL TOPLAM: ${grand.toLocaleString("tr-TR", { maximumFractionDigits: 0 })}₺*`;
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank");
  };

  return (
    <div className="space-y-4">
      {groupMenu && (
        <div
          data-testid="group-move-menu"
          onClick={(e) => e.stopPropagation()}
          className="fixed z-50 rounded-lg shadow-lg border overflow-hidden"
          style={{
            top: groupMenu.y, left: groupMenu.x,
            width: 300, maxHeight: 380,
            background: "white",
            borderColor: "hsl(var(--border))",
          }}
        >
          <div className="px-3 py-2 text-xs font-semibold border-b flex items-center justify-between"
               style={{ borderColor: "hsl(var(--border))", background: "hsl(var(--surface-1))" }}>
            <div className="truncate" title={groupMenu.item.name}>
              <span style={{ color: "hsl(var(--text-2))" }}>Taşı:</span>{" "}
              <span className="font-display font-semibold">{groupMenu.item.name}</span>
            </div>
            <button
              data-testid="group-move-close"
              onClick={() => setGroupMenu(null)}
              className="text-sm px-1 rounded hover:bg-black/5"
              style={{ color: "hsl(var(--text-2))" }}
            >×</button>
          </div>
          <div className="overflow-y-auto" style={{ maxHeight: 340 }}>
            {CATEGORIES.filter((c) => c.key !== "menu" && c.key !== "genel").map((cat) => {
              const subs = categoryTree?.[cat.key] || [];
              return (
                <div key={cat.key}>
                  <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider font-semibold"
                       style={{ background: "hsl(var(--surface-2))", color: "hsl(var(--text-2))" }}>
                    {cat.label}
                  </div>
                  {subs.length === 0 && (
                    <button
                      data-testid={`group-move-${cat.key}-empty`}
                      onClick={() => moveItemToGroup(cat.key, "")}
                      className={`w-full text-left px-3 py-1.5 text-sm hover:bg-black/5 flex items-center justify-between ${
                        groupMenu.item.category === cat.key && !groupMenu.item.subcategory ? "font-semibold" : ""
                      }`}
                      style={groupMenu.item.category === cat.key && !groupMenu.item.subcategory ? { color: "hsl(var(--primary))" } : {}}
                    >
                      <span>(alt-grupsuz)</span>
                      {groupMenu.item.category === cat.key && !groupMenu.item.subcategory && <span>✓</span>}
                    </button>
                  )}
                  {subs.map((sub) => {
                    const active = groupMenu.item.category === cat.key && groupMenu.item.subcategory === sub;
                    return (
                      <button
                        key={sub}
                        data-testid={`group-move-${cat.key}-${sub}`}
                        onClick={() => moveItemToGroup(cat.key, sub)}
                        className={`w-full text-left px-3 py-1.5 text-sm hover:bg-black/5 flex items-center justify-between ${active ? "font-semibold" : ""}`}
                        style={active ? { color: "hsl(var(--primary))" } : {}}
                        title={sub}
                      >
                        <span className="truncate pr-2">{sub}</span>
                        {active && <span>✓</span>}
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      )}
      {pricePopup && (
        <div
          data-testid="price-popup"
          onClick={(e) => { if (e.target === e.currentTarget) setPricePopup(null); }}
          className="fixed inset-0 z-40 flex items-start justify-center pt-24 px-4"
          style={{ background: "rgba(0,0,0,0.15)" }}
        >
          <div
            className="max-w-md w-full rounded-xl shadow-lg border p-4 flex items-start gap-3"
            style={{
              background: "white",
              borderColor: pricePopup.isError ? "#f5c2c7" : "hsl(var(--border))",
            }}
          >
            <div className="mt-0.5" style={{ color: pricePopup.isError ? "#c81e1e" : "#059669" }}>
              <i className={`fa-solid ${pricePopup.isError ? "fa-triangle-exclamation" : "fa-circle-check"} text-lg`}></i>
            </div>
            <div className="flex-1">
              <div className="font-medium text-sm">{pricePopup.title || "Bilgi"}</div>
              <div className="text-xs mt-0.5" style={{ color: "hsl(var(--text-2))" }}>{pricePopup.message}</div>
            </div>
            <button
              data-testid="price-popup-close"
              onClick={() => setPricePopup(null)}
              aria-label="Kapat"
              className="text-sm px-1.5 py-0.5 rounded hover:bg-black/5"
              style={{ color: "hsl(var(--text-2))" }}
            >
              ×
            </button>
          </div>
        </div>
      )}
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm" style={{ color: "hsl(var(--text-2))" }}>
            Tüm tutarlar ₺ · toplam <b>{fmtTRY(grandTotal)}</b>
          </p>
          <div
            data-testid="live-indicator"
            className="mt-1 inline-flex items-center gap-1.5 text-[11px] px-2 py-0.5 rounded-full"
            style={{
              backgroundColor: liveError ? "#FDECEA" : "hsl(var(--surface-2))",
              color: liveError ? "#9B2F28" : "hsl(var(--text-2))",
            }}
            title={
              liveError
                ? "Sunucuya ulaşılamadı, tekrar deneniyor"
                : "Liste her 3 saniyede bir otomatik güncelleniyor"
            }
          >
            <Radio
              className={`w-3 h-3 ${liveError ? "" : "animate-pulse"}`}
              style={{ color: liveError ? "#9B2F28" : "#16a34a" }}
            />
            {liveError
              ? "Bağlantı yok"
              : `Canlı${lastSync ? " · " + lastSync.toLocaleTimeString("tr-TR") : ""}`}
          </div>
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          <button
            data-testid="shopping-menu-pdf-btn"
            onClick={exportMenuPdf}
            disabled={exporting}
            title="Menü PDF: 7 gecelik menü programı"
            className="tk-btn-ghost !px-2.5 !py-1.5 text-xs disabled:opacity-60"
          >
            Menü
          </button>
          <button
            data-testid="shopping-pdf-boat-btn"
            onClick={exportXlsx}
            disabled={exporting}
            title="TEKNE için: Excel kumanya + menü (mutfak şablonu)"
            className="tk-btn-ghost !px-2.5 !py-1.5 text-xs disabled:opacity-60"
          >
            Tekne
          </button>
          <button
            data-testid="shopping-pdf-summary-btn"
            onClick={() => exportPdf("summary")}
            disabled={exporting}
            title="Kategori bazlı ÖZET PDF (sadece toplamlar)"
            className="tk-btn-ghost !px-2.5 !py-1.5 text-xs disabled:opacity-60"
          >
            Özet
          </button>
          <button
            data-testid="shopping-pdf-full-btn"
            onClick={() => exportPdf("full")}
            disabled={exporting}
            title="TAM: fiyatlarla tam liste"
            className="tk-btn-ghost !px-2.5 !py-1.5 text-xs disabled:opacity-60"
          >
            Tam
          </button>
          <button
            data-testid="shopping-whatsapp-btn"
            onClick={shareWhatsApp}
            title={tab === "menu" ? "Menüyü ve toplamları WhatsApp'ta paylaş" : "Alışveriş listesini WhatsApp'ta paylaş"}
            className="tk-btn-ghost !p-2.5"
          >
            <i className="fa-brands fa-whatsapp" style={{ color: "#25D366", fontSize: "1rem" }}></i>
          </button>
          <button
            data-testid="refresh-prices-btn"
            onClick={refreshPrices}
            disabled={estimating}
            title="Tahmini fiyatları yenile"
            className="tk-btn-ghost !p-2.5 disabled:opacity-60"
          >
            <RefreshCw className={`w-4 h-4 ${estimating ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>
      {estMsg && <div className="text-xs" style={{ color: "hsl(var(--text-2))" }}>{estMsg}</div>}

      {/* Main tabs — sticky. Mobilde 2 satırlı grid (5 sekme → 3+2), masaüstünde tek satır. */}
      <div
        className="p-1 rounded-xl sticky z-40 grid grid-cols-3 md:flex md:gap-1 gap-1 md:overflow-x-auto"
        style={{
          backgroundColor: "hsl(var(--surface-2))",
          top: "60px",
          boxShadow: "0 2px 10px -4px rgba(0,0,0,0.10)",
        }}
      >
        {CATEGORIES.map((c) => (
          <button
            key={c.key}
            data-testid={`shopping-tab-${c.key}`}
            onClick={() => { setTab(c.key); if (bulkEdit) cancelBulkEdit(); }}
            className={`md:flex-1 md:min-w-fit text-xs sm:text-sm md:text-base font-medium px-2 md:px-3 py-2 rounded-lg transition whitespace-nowrap ${
              tab === c.key ? "bg-white shadow-sm" : ""
            }`}
            style={{ color: tab === c.key ? "hsl(var(--text-1))" : "hsl(var(--text-2))" }}
          >
            {c.icon ? <span className="mr-1">{c.icon}</span> : null}{c.label}
          </button>
        ))}
      </div>

      {/* Menü tab content — embed Menu component */}
      {tab === "menu" && (
        <div className="pt-2">
          <Menu />
        </div>
      )}

      {/* Sub-category kulakçıkları (Yiyecek/İçecekler için).
          Mobil: sadece "Tümü" + "Diğer Gruplar" toggle görünür; toggle açınca kalan filtreler alta açılır.
          Masaüstü: hepsi tek satır (justify-between). */}
      {tab !== "menu" && (categoryTree[tab]?.length > 0) && (
        <div className="p-1 rounded-lg space-y-1" style={{ backgroundColor: "hsl(var(--surface-1))", border: "1px solid hsl(var(--border))" }}>
          <div className="flex items-center gap-1 md:justify-between md:flex-wrap">
            <button
              data-testid="shopping-subtab-all"
              onClick={() => setSubTab("__all__")}
              className={`text-xs px-3 py-1.5 rounded-md transition font-medium whitespace-nowrap ${
                subTab === "__all__" ? "bg-white shadow-sm" : ""
              }`}
              style={{ color: subTab === "__all__" ? "hsl(var(--primary))" : "hsl(var(--text-2))" }}
            >
              Tümü
            </button>
            {/* Masaüstünde tüm subtab'lar aynı satırda */}
            <div className="hidden md:flex md:flex-wrap md:gap-1 md:flex-1 md:justify-end">
              {(categoryTree[tab] || []).map((sub) => (
                <button
                  key={sub}
                  data-testid={`shopping-subtab-${sub}`}
                  onClick={() => setSubTab(sub)}
                  className={`text-xs px-3 py-1.5 rounded-md transition font-medium whitespace-nowrap ${
                    subTab === sub ? "bg-white shadow-sm" : ""
                  }`}
                  style={{ color: subTab === sub ? "hsl(var(--primary))" : "hsl(var(--text-2))" }}
                  title={sub}
                >
                  {sub.length > 30 ? sub.slice(0, 28) + "…" : sub}
                </button>
              ))}
            </div>
            {/* Mobilde tek Diğer Gruplar butonu */}
            <button
              data-testid="shopping-subtab-toggle"
              onClick={() => setShowOtherSubGroups((v) => !v)}
              className={`ml-auto md:hidden text-xs px-3 py-1.5 rounded-md font-medium whitespace-nowrap flex items-center gap-1 ${showOtherSubGroups ? "bg-white shadow-sm" : ""}`}
              style={{ color: "hsl(var(--text-2))" }}
            >
              Diğer Gruplar {showOtherSubGroups ? "▴" : "▾"}
            </button>
          </div>
          {/* Mobilde toggle açıksa alt gruplar altta */}
          {showOtherSubGroups && (
            <div className="md:hidden flex flex-wrap gap-1 pt-1">
              {(categoryTree[tab] || []).map((sub) => (
                <button
                  key={sub}
                  data-testid={`shopping-subtab-mob-${sub}`}
                  onClick={() => { setSubTab(sub); setShowOtherSubGroups(false); }}
                  className={`text-xs px-3 py-1.5 rounded-md transition font-medium whitespace-nowrap ${
                    subTab === sub ? "bg-white shadow-sm" : ""
                  }`}
                  style={{ color: subTab === sub ? "hsl(var(--primary))" : "hsl(var(--text-2))" }}
                  title={sub}
                >
                  {sub.length > 24 ? sub.slice(0, 22) + "…" : sub}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {tab !== "menu" && (
      <>
      {/* Sol: özet · Orta: arama · Sağ: Düzenle/Yeni */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="text-sm whitespace-nowrap" data-testid="shopping-summary" style={{ color: "hsl(var(--text-2))" }}>
          <b style={{ color: "hsl(var(--text-1))" }}>{filteredKalemCount}</b> Ürün
          <span className="mx-2" style={{ color: "hsl(var(--border))" }}>|</span>
          <span title={`Tam: ${fmtTRY(catTotal)}`}>~<b style={{ color: "hsl(var(--text-1))" }}>{catTotalRounded.toLocaleString("tr-TR", { maximumFractionDigits: 0 })}</b>₺</span>
        </div>
        <div className="relative flex-1 flex justify-center min-w-0">
          <div className="relative w-full max-w-md">
            <i className="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-xs" style={{ color: "hsl(var(--text-2))" }}></i>
            <input
              data-testid="shopping-search"
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Ürün ara (i/İ/ı/I fark etmez)"
              className="tk-input w-full !pl-9 !pr-8 !py-1.5 text-sm"
            />
            {search && (
              <button
                data-testid="shopping-search-clear"
                onClick={() => setSearch("")}
                title="Temizle"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-sm px-1.5 py-0.5 rounded hover:bg-black/5"
                style={{ color: "hsl(var(--text-2))" }}
              >
                ×
              </button>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!bulkEdit ? (
            <>
              <button data-testid="bulk-edit-btn" onClick={startBulkEdit} className="tk-btn-ghost flex items-center gap-1.5 text-sm" disabled={filtered.length === 0}>
                <Edit3 className="w-4 h-4" /> Düzenle
              </button>
              <button
                data-testid="add-shopping-btn"
                onClick={() => {
                  setShowNew(true);
                  setEditingId(null);
                  setDraft({ ...emptyDraft, category: tab });
                }}
                className="tk-btn flex items-center gap-1.5"
              >
                <Plus className="w-4 h-4" /> Yeni
              </button>
            </>
          ) : (
            <>
              <button data-testid="bulk-cancel-btn" onClick={cancelBulkEdit} className="tk-btn-ghost flex items-center gap-1.5 text-sm">
                <X className="w-4 h-4" /> Vazgeç
              </button>
              <button data-testid="bulk-save-btn" onClick={saveBulkEdit} className="tk-btn flex items-center gap-1.5">
                <Check className="w-4 h-4" /> Kaydet
              </button>
            </>
          )}
        </div>
      </div>

      {showNew && (
        <div className="tk-card p-4" data-testid="new-shopping-card">
          <ShoppingForm draft={draft} setDraft={setDraft} onSave={save} onCancel={() => { setShowNew(false); setDraft(emptyDraft); }} categoryTree={categoryTree} />
        </div>
      )}

      <div className="space-y-2">
        {filtered.length === 0 && !showNew && (
          <div className="tk-card p-6 text-center text-sm" style={{ color: "hsl(var(--text-2))" }}>
            Bu kategoride henüz kalem yok.
          </div>
        )}
        {displayList.map((entry, idx) => {
          if (entry.type === "header") {
            const isOpen = openGroup === entry.label;
            return (
              <button
                key={`hdr-${entry.label}-${idx}`}
                data-testid={`shopping-group-header-${entry.label}`}
                onClick={() => setOpenGroup(isOpen ? "__none__" : entry.label)}
                className="mt-1 w-full px-3 py-1.5 text-xs font-semibold uppercase tracking-wider rounded-md flex items-center justify-between text-left"
                style={{ backgroundColor: "hsl(var(--surface-2))", color: "hsl(var(--primary))" }}
              >
                <span className="flex items-center gap-2">
                  <span className="md:hidden text-[11px]" aria-hidden>{isOpen ? "▾" : "▸"}</span>
                  <span>{entry.label}</span>
                </span>
                <span className="text-[10px]" style={{ color: "hsl(var(--text-2))" }}>{entry.count} kalem</span>
              </button>
            );
          }
          // Mobilde accordion: sadece açık grubun kalemleri render'a girer.
          // Masaüstünde her zaman görünür (accordion sadece sm altında).
          const inClosedGroup = entry.groupLabel && openGroup && openGroup !== entry.groupLabel;
          const i = entry.item;
          const rowTotal = itemTotal(i);
          if (bulkEdit) {
            const d = bulkDraft[i.id] || {};
            // Bulk edit'te de effectiveQty mantığı: adet=0 ama not doluysa nottan sayı
            const liveDraftItem = {
              quantity: d.quantity,
              unit: d.unit,
              unit_cost_try: d.unit_cost_try,
              description: d.description,
              excluded_from_cost: i.excluded_from_cost,
            };
            const liveTotal = itemTotal(liveDraftItem);
            return (
              <div key={i.id} data-testid={`bulk-row-${i.id}`} className={`tk-card p-3 ${inClosedGroup ? "hidden md:block" : ""}`}>
                <div className="grid grid-cols-12 gap-2 items-start">
                  <div className="col-span-12 md:col-span-4">
                    <label className="tk-label">Ürün</label>
                    <input className="tk-input !py-2" value={d.name || ""} onChange={(e) => updBulk(i.id, "name", e.target.value)} />
                  </div>
                  <div className="col-span-3 md:col-span-2">
                    <label className="tk-label">Adet</label>
                    <input inputMode="decimal" className="tk-input !py-2" value={d.quantity ?? ""} onChange={(e) => updBulk(i.id, "quantity", e.target.value)} />
                  </div>
                  <div className="col-span-3 md:col-span-2">
                    <label className="tk-label">Birim</label>
                    <select className="tk-input !py-2" value={d.unit || "adet"} onChange={(e) => updBulk(i.id, "unit", e.target.value)}>
                      {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                    </select>
                  </div>
                  <div className="col-span-6 md:col-span-2">
                    <label className="tk-label">Birim ₺</label>
                    <input inputMode="decimal" className="tk-input !py-2" value={d.unit_cost_try ?? ""} onChange={(e) => updBulk(i.id, "unit_cost_try", e.target.value)} />
                  </div>
                  <div className="col-span-12 md:col-span-2">
                    <label className="tk-label">Toplam</label>
                    <div className="tk-input !py-2 !bg-transparent font-semibold text-sm" style={{ color: "hsl(var(--primary))" }}>{fmtTRY(liveTotal)}</div>
                  </div>
                  <div className="col-span-12">
                    <label className="tk-label">Açıklama</label>
                    <input className="tk-input !py-2" value={d.description || ""} onChange={(e) => updBulk(i.id, "description", e.target.value)} />
                  </div>
                </div>
              </div>
            );
          }
          if (editingId === i.id) {
            return (
              <div key={i.id} className="tk-card p-4" data-testid={`edit-shopping-${i.id}`}>
                <ShoppingForm draft={draft} setDraft={setDraft} onSave={save} onCancel={cancelEdit} categoryTree={categoryTree} />
              </div>
            );
          }
          return (
            <div
              key={i.id}
              data-testid={`shopping-row-${i.id}`}
              onContextMenu={(e) => { e.preventDefault(); openGroupMenu(e, i); }}
              title="Sağ tıkla → grubu değiştir"
              className={`tk-card px-4 py-2.5 items-center gap-3 ${inClosedGroup ? "hidden md:flex" : "flex"}`}
              style={i.done ? { backgroundColor: "hsla(141, 45%, 88%, 0.55)", borderColor: "hsla(141, 30%, 70%, 0.45)" } : undefined}
            >
              <button
                data-testid={`done-toggle-${i.id}`}
                onClick={() => toggleDone(i)}
                className="flex-shrink-0"
                title={i.done ? "Hazır işaretini kaldır" : "Hazır olarak işaretle"}
              >
                {i.done ? (
                  <CheckCircle2 className="w-5 h-5" style={{ color: "hsl(var(--success))" }} />
                ) : (
                  <Circle className="w-5 h-5" style={{ color: "hsl(var(--text-2))" }} />
                )}
              </button>
              <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
                <div className="font-display font-semibold truncate">{highlight(i.name)}</div>
                {i.brand && (
                  <span
                    data-testid={`brand-chip-${i.id}`}
                    onClick={(e) => { e.stopPropagation(); toggleExpand(i.id); }}
                    title={i.brand}
                    className={`text-xs px-1.5 py-0.5 rounded cursor-pointer ${expandedIds.has(i.id) ? "whitespace-normal break-words" : "max-w-[40vw] sm:max-w-none truncate"}`}
                    style={{ backgroundColor: "hsl(var(--surface-2))", color: "hsl(var(--text-2))" }}
                  >
                    {highlight(i.brand)}
                  </span>
                )}
                {i.description && (
                  <span
                    data-testid={`desc-chip-${i.id}`}
                    onClick={(e) => { e.stopPropagation(); toggleExpand(i.id); }}
                    title={i.description}
                    className={`tk-chip cursor-pointer ${expandedIds.has(i.id) ? "whitespace-normal break-words" : "max-w-[45vw] sm:max-w-none truncate"}`}
                  >
                    {highlight(i.description)}
                  </span>
                )}
              </div>
              <div className="hidden md:flex items-center gap-6 text-sm whitespace-nowrap">
                <div className="flex items-center justify-between" style={{ width: 90 }}>
                  <span className="text-[10px] uppercase tracking-wider" style={{ color: "hsl(var(--text-2))" }}>Adet</span>
                  <span className="font-medium">{(i.quantity || 0) > 0 ? `${i.quantity} ${i.unit || ""}`.trim() : "0"}</span>
                </div>
                <div className="flex items-center justify-between" style={{ width: 105 }}>
                  <span className="text-[10px] uppercase tracking-wider" style={{ color: "hsl(var(--text-2))" }}>Birim ₺</span>
                  <span className="font-medium">{fmtTRY(i.unit_cost_try || 0)}</span>
                </div>
                <div className="flex items-center justify-between" style={{ width: 125 }}>
                  <span className="text-[10px] uppercase tracking-wider" style={{ color: "hsl(var(--text-2))" }}>Toplam ₺</span>
                  <span className="font-semibold" style={{ color: "hsl(var(--primary))" }}>{fmtTRY(rowTotal)}</span>
                </div>
              </div>
              <div className="md:hidden text-xs text-right whitespace-nowrap" style={{ color: "hsl(var(--text-2))" }}>
                <div>{i.quantity || 0} {i.unit} · {fmtTRY(i.unit_cost_try || 0)}</div>
                <div className="font-semibold" style={{ color: "hsl(var(--primary))" }}>{fmtTRY(rowTotal)}</div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  data-testid={`move-group-btn-${i.id}`}
                  onClick={(e) => { e.stopPropagation(); openGroupMenu(e, i); }}
                  title="Grubu değiştir"
                  className="tk-btn-ghost !py-1.5 !px-2"
                ><FolderInput className="w-4 h-4" /></button>
                <button data-testid={`edit-shopping-btn-${i.id}`} onClick={() => startEdit(i)} className="tk-btn-ghost !py-1.5 !px-2"><Pencil className="w-4 h-4" /></button>
                <button
                  data-testid={`exclude-cost-btn-${i.id}`}
                  onClick={() => toggleExclude(i)}
                  title={i.excluded_from_cost ? "MALİYET DIŞI — dahil etmek için tıkla" : "Maliyet hesabından çıkar"}
                  className="tk-btn-ghost !py-1.5 !px-2"
                  style={{
                    color: i.excluded_from_cost ? "#ef4444" : "hsl(var(--text-2))",
                    backgroundColor: i.excluded_from_cost ? "rgba(239,68,68,0.10)" : undefined,
                  }}
                >
                  <Ban
                    className="w-4 h-4"
                    strokeWidth={i.excluded_from_cost ? 3 : 2}
                    style={{ color: i.excluded_from_cost ? "#ef4444" : "inherit" }}
                  />
                </button>
                <button data-testid={`delete-shopping-btn-${i.id}`} onClick={() => remove(i.id)} className="tk-btn-danger !py-1.5 !px-2"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Notlar bölümü (sadece Genel Liste sekmesinde göster) */}
      {tab === "genel" && (
        <div className="tk-card mt-3 p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="font-display font-semibold">📝 Notlar</div>
            {!shoppingNotesEdit ? (
              <button
                data-testid="shopping-notes-edit-btn"
                className="tk-btn-ghost !py-1 !px-2 text-xs flex items-center gap-1"
                onClick={() => { setShoppingNotesDraft(shoppingNotes); setShoppingNotesEdit(true); }}
              >
                <Edit3 className="w-3.5 h-3.5" /> Düzenle
              </button>
            ) : (
              <div className="flex gap-1">
                <button
                  data-testid="shopping-notes-save-btn"
                  onClick={saveShoppingNotes}
                  className="tk-btn !py-1 !px-2 text-xs"
                >Kaydet</button>
                <button
                  data-testid="shopping-notes-cancel-btn"
                  onClick={() => setShoppingNotesEdit(false)}
                  className="tk-btn-ghost !py-1 !px-2 text-xs"
                >Vazgeç</button>
              </div>
            )}
          </div>
          {shoppingNotesEdit ? (
            <textarea
              data-testid="shopping-notes-textarea"
              value={shoppingNotesDraft}
              onChange={(e) => setShoppingNotesDraft(e.target.value)}
              rows={5}
              className="tk-input w-full font-mono text-sm"
              placeholder="Alışveriş notları (Excel export'ta ALIŞVERİŞ NOTLARI bölümüne gelir)..."
            />
          ) : (
            <div className="text-sm whitespace-pre-wrap" style={{ color: "hsl(var(--text-2))" }}>
              {shoppingNotes || <span className="italic">Henüz not eklenmemiş.</span>}
            </div>
          )}
        </div>
      )}
      </>
      )}
    </div>
  );
}

function ShoppingForm({ draft, setDraft, onSave, onCancel, categoryTree }) {
  const upd = (k, v) => setDraft({ ...draft, [k]: v });
  const subs = (categoryTree && categoryTree[draft.category]) || [];
  return (
    <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
      <div className="md:col-span-2">
        <label className="tk-label">Kategori</label>
        <select className="tk-input" data-testid="shopping-cat-select" value={draft.category} onChange={(e) => upd("category", e.target.value)}>
          {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
      </div>
      <div className="md:col-span-4">
        <label className="tk-label">Alt Grup</label>
        <select
          className="tk-input"
          data-testid="shopping-subcat-select"
          value={draft.subcategory || ""}
          onChange={(e) => upd("subcategory", e.target.value)}
        >
          <option value="">— seçiniz —</option>
          {subs.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <div className="md:col-span-6">
        <label className="tk-label">Ürün Adı</label>
        <input data-testid="shopping-name-input" className="tk-input" value={draft.name} onChange={(e) => upd("name", e.target.value)} />
      </div>
      <div className="md:col-span-6">
        <label className="tk-label">Marka</label>
        <input data-testid="shopping-brand-input" className="tk-input" placeholder="ör. Efes, Sütaş, Migros" value={draft.brand || ""} onChange={(e) => upd("brand", e.target.value)} />
      </div>
      <div className="md:col-span-2">
        <label className="tk-label">Adet</label>
        <input data-testid="shopping-qty-input" className="tk-input" inputMode="decimal" value={draft.quantity} onChange={(e) => upd("quantity", e.target.value)} />
      </div>
      <div className="md:col-span-2">
        <label className="tk-label">Birim</label>
        <select data-testid="shopping-unit-select" className="tk-input" value={draft.unit} onChange={(e) => upd("unit", e.target.value)}>
          {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
        </select>
      </div>
      <div className="md:col-span-2">
        <label className="tk-label">Birim Maliyet (₺)</label>
        <input data-testid="shopping-unit-cost-input" className="tk-input" inputMode="decimal" value={draft.unit_cost_try} onChange={(e) => upd("unit_cost_try", e.target.value)} />
      </div>
      <div className="md:col-span-6">
        <label className="tk-label">Açıklama</label>
        <input data-testid="shopping-desc-input" className="tk-input" value={draft.description} onChange={(e) => upd("description", e.target.value)} />
      </div>
      <div className="md:col-span-6 flex items-center gap-2 justify-end">
        <button data-testid="shopping-cancel-btn" onClick={onCancel} className="tk-btn-ghost flex items-center gap-1.5"><X className="w-4 h-4" /> Vazgeç</button>
        <button data-testid="shopping-save-btn" onClick={onSave} className="tk-btn flex items-center gap-1.5"><Check className="w-4 h-4" /> Kaydet</button>
      </div>
    </div>
  );
}
